/**
 * Quantity: a scalar value in canonical base units plus a dimension vector.
 *
 * Canonical base per physical dimension:
 *   data → byte, time → second, custom → the unit itself (factor 1).
 *
 * A rate like `10k img/d` is stored as value-per-second with dims {img:1, time:-1}.
 * `display.time` remembers the mentioned denominator ("keep the mentioned scale").
 */

export type DimMap = Map<string, number>; // dimKey (name + optional #tag) → exponent

export interface Quantity {
  value: number;
  dims: DimMap;
  /** mentioned time denominator, e.g. "d" — drives default render scale */
  time?: string;
  /** produced by a capacity divide (demand ÷ cap); already the answer — don't re-drill */
  solved?: boolean;
}

export function scalar(value: number): Quantity {
  return { value, dims: new Map() };
}

export function cloneDims(d: DimMap): DimMap {
  return new Map(d);
}

function addExp(d: DimMap, key: string, delta: number): void {
  const next = (d.get(key) ?? 0) + delta;
  if (next === 0) d.delete(key);
  else d.set(key, next);
}

export function mul(a: Quantity, b: Quantity): Quantity {
  const dims = cloneDims(a.dims);
  for (const [k, e] of b.dims) addExp(dims, k, e);
  // rate × time cancels to an absolute count (no /period annotation);
  // `10k img/d * y` = the amount over a year. Annualize with `= ?/y`.
  const out: Quantity = { value: a.value * b.value, dims };
  carryTime(out, a, b);
  return out;
}

export function div(a: Quantity, b: Quantity): Quantity {
  if (b.value === 0) throw new CalcError("division by zero");
  const dims = cloneDims(a.dims);
  for (const [k, e] of b.dims) addExp(dims, k, -e);
  const out: Quantity = { value: a.value / b.value, dims };
  carryTime(out, a, b);
  return out;
}

export function add(a: Quantity, b: Quantity, op: "+" | "-"): Quantity {
  if (!sameDims(a.dims, b.dims)) {
    throw new CalcError(`cannot ${op === "+" ? "add" : "subtract"} different dimensions`);
  }
  const value = op === "+" ? a.value + b.value : a.value - b.value;
  return { value, dims: cloneDims(a.dims), time: a.time ?? b.time };
}

/** Raise a quantity (used for unit exponents) — integer power. */
export function pow(q: Quantity, n: number): Quantity {
  const dims: DimMap = new Map();
  for (const [k, e] of q.dims) {
    const v = e * n;
    if (v !== 0) dims.set(k, v);
  }
  return { value: q.value ** n, dims };
}

function carryTime(out: Quantity, a: Quantity, b: Quantity): void {
  const t = a.time ?? b.time;
  if (t !== undefined && [...out.dims].some(([k]) => k === "time")) out.time = t;
}

export function sameDims(a: DimMap, b: DimMap): boolean {
  if (a.size !== b.size) return false;
  for (const [k, e] of a) if (b.get(k) !== e) return false;
  return true;
}

/** Stable signature for merging states by dimension shape. */
export function signature(d: DimMap): string {
  return [...d.entries()].sort((x, y) => (x[0] < y[0] ? -1 : 1)).map(([k, e]) => `${k}^${e}`).join(",");
}

export class CalcError extends Error {}
